import { useState, useEffect } from "react";
import { getAllUsers, createUser, updateUser, deleteUser, toggleUserStatus } from "../../services/AdminService";
import { EyeIcon, EyeOffIcon, PlusIcon, PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";

const UserModal = ({ isOpen, onClose, onSubmit, title, isEdit = false, formData, handleChange, showPassword, setShowPassword, showConfirmPassword, setShowConfirmPassword, passwordError }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Red Hat Display' }}>{title}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-row gap-4">
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">First Name</label>
                            <input
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type="text"
                                placeholder="Kevin"
                                name="firstname"
                                value={formData.firstname || ''}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Last Name</label>
                            <input
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type="text"
                                placeholder="Tan"
                                name="lastname"
                                value={formData.lastname || ''}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Email</label>
                        <input
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            type="email"
                            placeholder="kevin@socium.sg"
                            name="email"
                            value={formData.email || ''}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Role</label>
                        <select
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            name="role_name"
                            value={formData.role_name || ''}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Role</option>
                            <option value="admin">Admin</option>
                            <option value="subscriber">Subscriber</option>
                            <option value="free">Free</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Password {isEdit && "(Leave empty to keep current)"}</label>
                        <div className="relative flex items-center">
                            <input
                                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type={showPassword ? "text" : "password"}
                                placeholder="********"
                                name="password"
                                value={formData.password || ''}
                                onChange={handleChange}
                                required={!isEdit}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center mr-2 text-orange-500"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Confirm Password</label>
                        <div className="relative flex items-center">
                            <input
                                className={`w-full px-4 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${passwordError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-orange-500'
                                    }`}
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="********"
                                name="confirmPassword"
                                value={formData.confirmPassword || ''}
                                onChange={handleChange}
                                required={!isEdit || formData.password}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center mr-2 text-orange-500"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                            </button>
                        </div>
                        {passwordError && (
                            <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive || false}
                            onChange={handleChange}
                            className="w-4 h-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label className="text-sm font-medium">Active User</label>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm text-white rounded-md bg-orange hover:bg-opacity-90 transition-colors duration-200"
                        >
                            {isEdit ? 'Update' : 'Create'} User
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    // Sorting states
    const [sortField, setSortField] = useState("createdAt");
    const [sortDirection, setSortDirection] = useState("desc");

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        role_name: '',
        isActive: true,
        password: '',
        confirmPassword: ''
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await getAllUsers();
            if (response && Array.isArray(response.Data)) {
                setUsers(response.Data);
            } else {
                setError("Received data structure is not as expected");
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setError(error.message || "An error occurred while fetching users");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();

        // Check if passwords match
        if (formData.password !== formData.confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        try {
            const response = await createUser(formData);
            if (response["Base Output"].status_code === 201 || response["Base Output"].status_code === 200) {
                alert(response["Base Output"].message);
                setShowCreateModal(false);
                setFormData({
                    email: '',
                    firstname: '',
                    lastname: '',
                    role_name: '',
                    isActive: true,
                    password: '',
                    confirmPassword: ''
                });
                fetchUsers();
            } else {
                alert(response["Base Output"].message || 'Failed to create user');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to create user');
        }
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();

        // Check if passwords match (only if password is being updated)
        if (formData.password && formData.password !== formData.confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        try {
            const updateData = { ...formData };
            if (!updateData.password) {
                delete updateData.password;
            }
            const response = await updateUser(selectedUser._id.$oid, updateData);
            if (response["Base Output"].status_code === 200) {
                alert(response["Base Output"].message);
                setShowEditModal(false);
                setSelectedUser(null);
                setFormData({
                    email: '',
                    firstname: '',
                    lastname: '',
                    role_name: '',
                    isActive: true,
                    password: '',
                    confirmPassword: ''
                });
                fetchUsers();
            } else {
                alert(response["Base Output"].message || 'Failed to update user');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to update user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                const response = await deleteUser(userId);
                if (response.status_code === 200) {
                    alert('User deleted successfully');
                    fetchUsers();
                } else {
                    alert(response.message || 'Failed to delete user');
                }
            } catch (error) {
                alert(error.response?.data?.message || error.message || 'Failed to delete user');
            }
        }
    };

    const handleToggleStatus = async (userId, currentStatus) => {
        try {
            const response = await toggleUserStatus(userId, !currentStatus);
            if (response.status_code === 200) {
                alert(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
                fetchUsers();
            } else {
                alert(response.message || 'Failed to update user status');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to update user status');
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear password error when user starts typing
        if (name === 'password' || name === 'confirmPassword') {
            setPasswordError('');
        }
    };

    const openCreateModal = () => {
        setFormData({
            email: '',
            firstname: '',
            lastname: '',
            role_name: '',
            isActive: true,
            password: '',
            confirmPassword: ''
        });
        setShowCreateModal(true);
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            email: user.email,
            firstname: user.firstname,
            lastname: user.lastname,
            role_name: user.role_name,
            isActive: user.isActive,
            password: '',
            confirmPassword: ''
        });
        setShowEditModal(true);
    };

    const closeModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData({
            email: '',
            firstname: '',
            lastname: '',
            role_name: '',
            isActive: true,
            password: '',
            confirmPassword: ''
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setPasswordError('');
    };

    // Sorting function
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    // Filter and sort users
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === "all" || user.role_name === roleFilter;
        const matchesStatus = statusFilter === "all" ||
            (statusFilter === "active" && user.isActive) ||
            (statusFilter === "inactive" && !user.isActive);

        return matchesSearch && matchesRole && matchesStatus;
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
            case 'name':
                aValue = `${a.firstname} ${a.lastname}`.toLowerCase();
                bValue = `${b.firstname} ${b.lastname}`.toLowerCase();
                break;
            case 'email':
                aValue = a.email.toLowerCase();
                bValue = b.email.toLowerCase();
                break;
            case 'role_name':
                aValue = a.role_name.toLowerCase();
                bValue = b.role_name.toLowerCase();
                break;
            case 'isActive':
                aValue = a.isActive ? 1 : 0;
                bValue = b.isActive ? 1 : 0;
                break;
            case 'createdAt':
            default:
                aValue = new Date(a.createdAt.$date);
                bValue = new Date(b.createdAt.$date);
                break;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

    // Pagination controls
    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const SortableHeader = ({ field, children, align = 'left' }) => (
        <th
            className={`px-6 py-4 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200`}
            onClick={() => handleSort(field)}
        >
            <div className="flex items-center space-x-1">
                <span>{children}</span>
                <div className="flex flex-col">
                    <ChevronUpIcon
                        className={`w-3 h-3 ${sortField === field && sortDirection === 'asc' ? 'text-orange-500' : 'text-gray-400'}`}
                    />
                    <ChevronDownIcon
                        className={`w-3 h-3 -mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-orange-500' : 'text-gray-400'}`}
                    />
                </div>
            </div>
        </th>
    );


    return (
        <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={openCreateModal}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-md bg-orange hover:bg-opacity-90 transition-colors duration-200"
                >
                    <PlusIcon className="w-4 h-4" />
                    Create User
                </button>
            </div>

            {/* Filters */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Search</label>
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Role</label>
                    <select
                        value={roleFilter}
                        onChange={(e) => {
                            setRoleFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Roles</option>
                        <option value="admin">Admin</option>
                        <option value="subscriber">Subscriber</option>
                        <option value="free user">Free</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Items per page</label>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            {/* Users Table */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            ) : (
                <>
                    {/* Data Count Info */}
                    <div className="mb-4 flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-700">
                                <span className="font-medium text-gray-900">{filteredUsers.length}</span>
                                {filteredUsers.length === 1 ? ' user' : ' users'} found
                                {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
                                    <span className="text-gray-500"> (filtered from {users.length} total)</span>
                                )}
                            </div>
                            {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
                                <div className="flex items-center space-x-2">
                                    {searchTerm && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Search: "{searchTerm}"
                                        </span>
                                    )}
                                    {roleFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            Role: {roleFilter}
                                        </span>
                                    )}
                                    {statusFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Status: {statusFilter}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setRoleFilter('all');
                                    setStatusFilter('all');
                                    setCurrentPage(1);
                                }}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>

                    <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <SortableHeader field="name">Name</SortableHeader>
                                        <SortableHeader field="email">Email</SortableHeader>
                                        <SortableHeader field="role_name" align="center">ACCOUNT Role</SortableHeader>
                                        <SortableHeader field="isActive">Status</SortableHeader>
                                        <SortableHeader field="createdAt">Created At</SortableHeader>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedUsers.length > 0 ? (
                                        paginatedUsers.map((user, index) => (
                                            <tr key={typeof user._id === 'string' ? user._id : `user-${index}-${user.email}`} className="hover:bg-gray-50 transition-colors duration-200">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                                                <span className="text-sm font-medium text-gray-700">
                                                                    {user.firstname.charAt(0)}{user.lastname.charAt(0)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {user.firstname} {user.lastname}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {user.email}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border justify-center items-center w-24 ${ // Added w-24, justify-center, items-center
                                                        user.role_name === 'admin'
                                                            ? 'bg-gradient-to-r from-gray-800 to-gray-700 text-white border-gray-600 shadow-lg transform hover:scale-105 transition-transform duration-200 ease-in-out' :
                                                            user.role_name === 'subscriber'
                                                                ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white border-orange-500 shadow-lg transform hover:scale-105 transition-transform duration-200 ease-in-out' :
                                                                user.role_name === 'free user'
                                                                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white border-green-500 shadow-lg transform hover:scale-105 transition-transform duration-200 ease-in-out'
                                                                    : 'bg-gray-100 text-gray-800 border-gray-300 shadow-md'
                                                        }`}>
                                                        {user.role_name.charAt(0).toUpperCase() + user.role_name.slice(1)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <button
                                                        onClick={() => handleToggleStatus(user._id, user.isActive)}
                                                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer transition-colors duration-200 ${user.isActive
                                                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                                                            }`}
                                                    >
                                                        {user.isActive ? 'Active' : 'Inactive'}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {new Date(user.createdAt.$date).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex items-center space-x-3">
                                                        <button
                                                            onClick={() => openEditModal(user)}
                                                            className="text-orange-600 hover:text-orange-900 transition-colors duration-200"
                                                            title="Edit User"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteUser(user._id.$oid)}
                                                            className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                                            title="Delete User"
                                                            disabled={user.role_name === 'admin'}
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                                    </svg>
                                                    <p className="text-lg font-medium">No users found</p>
                                                    <p className="text-sm">Try adjusting your search or filter criteria</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing{' '}
                                        <span className="font-medium">{startIndex + 1}</span>
                                        {' '}to{' '}
                                        <span className="font-medium">
                                            {Math.min(startIndex + itemsPerPage, sortedUsers.length)}
                                        </span>
                                        {' '}of{' '}
                                        <span className="font-medium">{sortedUsers.length}</span>
                                        {' '}results
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeftIcon className="h-5 w-5" />
                                        </button>

                                        {/* Page numbers */}
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;

                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                                        ? 'z-10 bg-orange-500 border-orange-500 text-white'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRightIcon className="h-5 w-5" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Create User Modal */}
            <UserModal
                isOpen={showCreateModal}
                onClose={closeModals}
                onSubmit={handleCreateUser}
                title="Create New User"
                formData={formData}
                handleChange={handleChange}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                showConfirmPassword={showConfirmPassword}
                setShowConfirmPassword={setShowConfirmPassword}
                passwordError={passwordError}
            />

            {/* Edit User Modal */}
            <UserModal
                isOpen={showEditModal}
                onClose={closeModals}
                onSubmit={handleUpdateUser}
                title="Edit User"
                isEdit={true}
                formData={formData}
                handleChange={handleChange}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                showConfirmPassword={showConfirmPassword}
                setShowConfirmPassword={setShowConfirmPassword}
                passwordError={passwordError}
            />
        </div>
    );
};

export default UserManagementPage;
